import { ScoringResult } from './scoringEngine';
import { DIMENSION_ORDER } from './dimensionMeta';

export interface DevelopmentComposite {
  score: number;            // 0–100
  zone: 'low' | 'medium' | 'high';
  zoneLabel: string;
  zoneColor: string;
  dimensionMean: number;    // 0–100
  topAmplitude: number;     // 0–100
  weakest: { id: string; percent: number };
  strongest: { id: string; percent: number };
  topPendulum: { id: string; name: string; amplitude: number } | null;
  topBlocker: { id: string; percent: number } | null;
}

// Development Score composite formula.
//   Average of the percents for the tracks the user has actually TAKEN. A track
//   with no completed pass is absent from `scores.dimensions` and is skipped —
//   averaging it in as 0 would read as "you scored badly there" when the real
//   state is "we never asked", and one track out of eight would cap the
//   headline score at ~12%.
// We use Math.max for the divisor to avoid div-by-zero on empty data.
export function calculateDevelopmentScore(scores: ScoringResult): DevelopmentComposite {
  const measured = DIMENSION_ORDER.filter(d => scores.dimensions[d] !== undefined);
  const dimPercents = measured.map(d => scores.dimensions[d].percent);
  const dimensionMean = dimPercents.reduce((a, b) => a + b, 0) / Math.max(1, dimPercents.length);

  const sortedPendulums = [...scores.pendulums].sort((a, b) => b.amplitude - a.amplitude);
  const topPendulum = sortedPendulums[0] || null;
  const topAmplitudePct = topPendulum ? Math.round(topPendulum.amplitude * 100) : 0;

  const score = Math.round(dimensionMean);

  // Weakest/strongest are only meaningful among measured tracks.
  const sortedDims = measured
    .map(id => ({ id, percent: scores.dimensions[id].percent }))
    .sort((a, b) => a.percent - b.percent);

  // A user who has paid but not yet finished a track has nothing measured;
  // keep the shape intact so the dashboard renders its empty state instead of
  // dereferencing undefined.
  const noneMeasured = { id: '', percent: 0 };
  const weakest = sortedDims[0] ?? noneMeasured;
  const strongest = sortedDims[sortedDims.length - 1] ?? noneMeasured;

  const topBlocker = scores.tags.find(t => t.type === 'shadow' && t.raw > 0) || null;

  let zone: 'low' | 'medium' | 'high';
  let zoneLabel: string;
  let zoneColor: string;
  if (score < 50) {
    zone = 'low';
    zoneLabel = 'Foundation';
    zoneColor = '#EF4444';
  } else if (score < 70) {
    zone = 'medium';
    zoneLabel = 'Developing';
    zoneColor = '#F59E0B';
  } else {
    zone = 'high';
    zoneLabel = 'Integrated';
    zoneColor = '#22C55E';
  }

  return {
    score,
    zone,
    zoneLabel,
    zoneColor,
    dimensionMean: Math.round(dimensionMean),
    topAmplitude: topAmplitudePct,
    weakest,
    strongest,
    topPendulum: topPendulum
      ? { id: topPendulum.id, name: topPendulum.name, amplitude: topPendulum.amplitude }
      : null,
    topBlocker: topBlocker ? { id: topBlocker.id, percent: topBlocker.percent } : null,
  };
}
